import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import isCreativeTypeNode from 'common/src/components/CLCardsMap/utils/isCreativeTypeNode';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {addCard} from 'common/src/modules/campaigns/store/campaignLogicTab/actions/addCard';
import {setShouldAutoArrange} from 'common/src/modules/campaigns/store/campaignLogicTab/campaignLogic.slice';
import {
  CreativeNodePlaceholderCreativeId,
  DTCreativeNode,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {MapNode} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const duplicateCardToolSchema = z.object({
  cardId: z.string().describe(describeIdFormat(IdPrefix.Card)),
});

export default function duplicateCardTool(store: CLStore) {
  async function execute({
    input: {cardId},
  }: ToolExecuteParams<typeof duplicateCardToolSchema>): Promise<ToolResult> {
    const parseResult = idHelper.parseCardId(cardId);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const numericId = parseResult.numericId;
    const {campaignLogic} = store.getState().campaignPage;
    const {dispatch} = store;

    const card = store.getState().campaignPage.campaignLogic.nodes.campaign[numericId];

    if (!card) {
      return toolError(`Card ${idHelper.toCardId(numericId)} not found`);
    }

    const nextId = campaignLogic.nextNodeId;

    const duplicatedCard: MapNode = {
      ...card,
      id: nextId,
      outputs: card.outputs.map((output) => ({
        ...output,
        nodeId: NO_NODE_ID,
        node: null,
      })),
      inputs: [],
    };

    if (isCreativeTypeNode(card.type)) {
      const creativeNode = duplicatedCard as Omit<DTCreativeNode, 'landingPageSource'>;
      creativeNode.creativeId = CreativeNodePlaceholderCreativeId;
      creativeNode.creative = undefined;

      await dispatch(addCard({newNode: creativeNode, campaignTreeType: CampaignTreeType.Campaign}));
    } else {
      await dispatch(
        addCard({newNode: duplicatedCard, campaignTreeType: CampaignTreeType.Campaign}),
      );
    }

    dispatch(setShouldAutoArrange(true));

    return toolResult(
      `Card ${idHelper.toCardId(numericId)} duplicated successfully. New card ID is ${idHelper.toCardId(nextId)}.`,
    );
  }

  return tool({
    name: CampaignSetupAgentToolName.DuplicateCard,
    displayName: 'Duplicating the card',
    description: 'Duplicates a SINGLE card. Use this tool to duplicate a single card data.',
    execute,
    inputSchema: duplicateCardToolSchema,
  });
}
